import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { loginSchema, verifyTotpSchema, type LoginInput, type LoginResponse, type TotpSetupResponse, type VerifyTotpInput } from '@bifeai/shared';
import { BrandStacked, Button, Callout, Field, Input, Spinner } from '@bifeai/ui';
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { reloadMe, useMeQuery } from '../lib/session';

function AdminAuth({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <div className="bf-center-screen" style={{ background: 'var(--bf-canvas)', padding: 24 }}>
      <div className="bf-card" style={{ width: 'min(420px, 100%)', padding: 32, display: 'grid', gap: 22 }}>
        <div style={{ display: 'grid', justifyItems: 'center', gap: 6 }}>
          <BrandStacked className="bf-brand-stacked" />
          <small className="bf-brand-suffix">Panel admin</small>
        </div>
        <div>
          <h1 className="bf-auth-title">{title}</h1>
          <p className="bf-auth-sub">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

const CodeInput = (props: ComponentProps<typeof Input>) => (
  <Input className="bf-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" {...props} />
);

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const res = await api.post<LoginResponse>('/platform/auth/login', values);
      await reloadMe(qc);
      navigate(res.next === 'verify_mfa' ? '/ingresar/codigo' : '/activar-2fa', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AdminAuth title="Panel de administración" subtitle="Acceso exclusivo para el equipo de BifeAI.">
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Email" error={formState.errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus {...register('email')} />
        </Field>
        <Field label="Contraseña" error={formState.errors.password?.message}>
          <Input type="password" autoComplete="current-password" {...register('password')} />
        </Field>
        <Button type="submit" variant="primary" block loading={formState.isSubmitting}>
          Ingresar
        </Button>
      </form>
    </AdminAuth>
  );
}

export function VerifyCodePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMeQuery();
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<VerifyTotpInput>({ resolver: zodResolver(verifyTotpSchema) });

  if (me.isPending)
    return (
      <div className="bf-center-screen">
        <Spinner />
      </div>
    );
  if (me.isError) return <Navigate to="/ingresar" replace />;
  if (me.data.session.mfaVerified) return <Navigate to="/" replace />;
  if (me.data.session.mfaSetupRequired) return <Navigate to="/activar-2fa" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/platform/auth/2fa/verify', values);
      await reloadMe(qc);
      navigate('/', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AdminAuth title="Código de seguridad" subtitle="Ingresá el código de 6 números de BifeAI Admin.">
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Código" error={formState.errors.code?.message}>
          <CodeInput autoFocus {...register('code')} />
        </Field>
        <Button type="submit" variant="primary" block loading={formState.isSubmitting}>
          Verificar
        </Button>
      </form>
    </AdminAuth>
  );
}

export function SetupTwoFactorPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMeQuery();
  const [formError, setFormError] = useState<string | null>(null);
  const setup = useMutation({ mutationFn: () => api.post<TotpSetupResponse>('/platform/auth/2fa/setup') });
  const { register, handleSubmit, formState } = useForm<VerifyTotpInput>({ resolver: zodResolver(verifyTotpSchema) });
  const needsSetup = Boolean(me.data && !me.data.admin.totpEnabled);
  const started = useRef(false);
  const { mutate } = setup;
  useEffect(() => {
    if (needsSetup && !started.current) {
      started.current = true;
      mutate();
    }
  }, [needsSetup, mutate]);

  if (me.isPending)
    return (
      <div className="bf-center-screen">
        <Spinner />
      </div>
    );
  if (me.isError) return <Navigate to="/ingresar" replace />;
  if (me.data.admin.totpEnabled) return <Navigate to={me.data.session.mfaVerified ? '/' : '/ingresar/codigo'} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/platform/auth/2fa/enable', values);
      await reloadMe(qc);
      navigate('/', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AdminAuth
      title="Activá la verificación en dos pasos"
      subtitle="Es obligatoria para entrar al panel. Escaneá el código con tu app de autenticación."
    >
      {setup.isPending && <Spinner />}
      {setup.isError && <Callout tone="danger">{errorMessage(setup.error)}</Callout>}
      {setup.data && (
        <>
          <div className="bf-qr">
            <img src={setup.data.qrDataUrl} width={200} height={200} alt="Código QR para la app de autenticación" />
          </div>
          <p className="bf-secret">{setup.data.secret.match(/.{1,4}/g)?.join(' ')}</p>
        </>
      )}
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Código de la app" error={formState.errors.code?.message}>
          <CodeInput {...register('code')} />
        </Field>
        <Button type="submit" variant="primary" block loading={formState.isSubmitting} disabled={!setup.data}>
          Activar y entrar
        </Button>
      </form>
    </AdminAuth>
  );
}
